import React, { useState, useEffect } from 'react';
import { fetchApplicants, fetchApplicantStats, fetchCenters } from '../services/dashboard';
import BarChartComponent from '../components/BarChartComponent';
import CurveChartComponent from '../components/CurveChartComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faBuilding, faChartLine, faSearch, faFilter, faDownload, faBook } from '@fortawesome/free-solid-svg-icons';
import { exportToExcel } from '../utils/exportToExcel';
import { ORGANIZATION_CITY_RELATION } from '../constants/constants';

const ApplicantsDashboard = () => {
    const [applicants, setApplicants] = useState([]);
    const [stats, setStats] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        organizationId: '',
        search: '',
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        loadData();
        loadOrganizations();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [applicantsData, statsData] = await Promise.all([
                fetchApplicants(filters),
                fetchApplicantStats(filters.organizationId || null)
            ]);
            setApplicants(applicantsData.data || []);
            setStats(statsData.stats || null);
        } catch (error) {
            console.error('Error loading applicants data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadOrganizations = async () => {
        try {
            const centersData = await fetchCenters();
            setOrganizations(centersData.centers || []);
        } catch (error) {
            console.error('Error loading organizations:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        loadData();
    };

    const resetFilters = () => {
        setFilters({
            organizationId: '',
            search: '',
            startDate: '',
            endDate: ''
        });
        setTimeout(() => loadData(), 100);
    };

    const handleExport = () => {
        const columns = [
            { header: 'الاسم الكامل', key: '_fullName' },
            { header: 'المدينة', key: '_city' },
            { header: 'المركز', key: 'org.name' },
            { header: 'التخصص', key: 'curriculum.code' },
            { header: 'المؤهل', key: 'certification' },
            { header: 'الهاتف', key: 'phone' },
            { header: 'الواتساب', key: 'whatsapp' },
            { header: 'البريد الإلكتروني', key: 'email' },
            { header: 'تاريخ التسجيل', key: '_createdAt' }
        ];

        const dataWithComputed = applicants.map(a => ({
            ...a,
            _fullName: `${a.first_name || ''} ${a.second_name || ''} ${a.third_name || ''} ${a.fourth_name || ''}`.trim(),
            _city: ORGANIZATION_CITY_RELATION[a.vtc] || '',
            _createdAt: a.createdAt ? new Date(a.createdAt).toLocaleDateString('ar-EG') : ''
        }));

        exportToExcel(dataWithComputed, columns, `applicants_${new Date().toISOString().split('T')[0]}`);
    };

    // Prepare chart data
    const orgChartData = stats?.byOrganization?.map(org => ({
        name: org.org?.name || 'غير محدد',
        value: parseInt(org.count) || 0
    })) || [];

    const ageGroupChartData = stats?.byAgeGroup?.map(ag => ({
        name: ag.ageGroup || 'غير محدد',
        value: parseInt(ag.count) || 0
    })) || [];

    const specializationChartData = stats?.bySpecialization?.map(spec => ({
        name: spec.curriculum?.code || 'غير محدد',
        value: parseInt(spec.count) || 0
    })) || [];

    const monthlyChartData = stats?.byMonth?.map(m => ({
        month: m.month || '',
        performance: parseInt(m.count) || 0
    })) || [];

    return (
        <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #1a1f3a 0%, #2d3347 100%)' }}>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <FontAwesomeIcon icon={faUsers} className="text-yellow-400" />
                    لوحة المتقدمين
                </h1>
                <p className="text-gray-400">عرض وتحليل بيانات المتقدمين للمراكز</p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">إجمالي المتقدمين</p>
                            <h3 className="text-3xl font-bold text-yellow-400">{stats?.total || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faUsers} className="text-4xl text-yellow-400 opacity-20" />
                    </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">عدد المراكز</p>
                            <h3 className="text-3xl font-bold text-blue-400">{stats?.byOrganization?.length || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faBuilding} className="text-4xl text-blue-400 opacity-20" />
                    </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm mb-1">عدد التخصصات</p>
                            <h3 className="text-3xl font-bold text-green-400">{stats?.bySpecialization?.length || 0}</h3>
                        </div>
                        <FontAwesomeIcon icon={faBook} className="text-4xl text-green-400 opacity-20" />
                    </div>
                </div>
            </div>

            {/* Filters Panel */}
            <div className="rounded-2xl p-6 mb-8" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                <h2 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} />
                    تصفية البيانات
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-gray-300 text-sm mb-2">المركز</label>
                        <select
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.organizationId}
                            onChange={(e) => handleFilterChange('organizationId', e.target.value)}
                        >
                            <option value="">الكل</option>
                            {organizations.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-300 text-sm mb-2">من تاريخ</label>
                        <input
                            type="date"
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-300 text-sm mb-2">إلى تاريخ</label>
                        <input
                            type="date"
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>

                    <div className="lg:col-span-3">
                        <label className="block text-gray-300 text-sm mb-2">بحث (الاسم)</label>
                        <input
                            type="text"
                            placeholder="ابحث بالاسم..."
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-yellow-400 focus:outline-none"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={applyFilters}
                        className="px-6 py-2 bg-yellow-400 text-gray-900 rounded-lg font-bold hover:bg-yellow-500 transition-colors flex items-center gap-2"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                        تطبيق
                    </button>
                    <button
                        onClick={resetFilters}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-colors"
                    >
                        إعادة تعيين
                    </button>
                    <button
                        onClick={handleExport}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center gap-2 mr-auto"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        تصدير Excel
                    </button>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <BarChartComponent
                    data={orgChartData}
                    title="متقدمين حسب المركز"
                    dataKey="value"
                    nameKey="name"
                    loading={loading}
                    height={350}
                />

                <BarChartComponent
                    data={ageGroupChartData}
                    title="متقدمين حسب الفئة العمرية"
                    dataKey="value"
                    nameKey="name"
                    loading={loading}
                    height={350}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <BarChartComponent
                    data={specializationChartData}
                    title="اعلي 10 تخصصات"
                    dataKey="value"
                    nameKey="name"
                    loading={loading}
                    height={350}
                />

                <CurveChartComponent
                    data={monthlyChartData}
                    title="المتقدمون شهرياً (آخر 12 شهر)"
                    dataKeys={['performance']}
                    loading={loading}
                    height={350}
                />
            </div>

            {/* Data Table */}
            <div className="rounded-2xl overflow-hidden" style={{ background: '#2d3347', boxShadow: '0 2px 12px #0004' }}>
                <div className="p-6">
                    <h2 className="text-xl font-bold text-yellow-400 mb-4">قائمة المتقدمين</h2>

                    {loading ? (
                        <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
                    ) : applicants.length === 0 ? (
                        <div className="text-center text-gray-400 py-12">لا توجد بيانات متاحة</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-600">
                                        <th className="text-right p-3 text-gray-300 font-semibold">#</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">الاسم الكامل</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">المدينة</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">المركز</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">التخصص</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">المؤهل</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">الواتساب</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">الهاتف</th>
                                        <th className="text-right p-3 text-gray-300 font-semibold">تاريخ التسجيل</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applicants.map((applicant, index) => (
                                        <tr key={applicant.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                            <td className="p-3 text-gray-300">{index + 1}</td>
                                            <td className="p-3 text-white font-medium">
                                                {applicant.first_name} {applicant.second_name} {applicant.third_name} {applicant.fourth_name}
                                            </td>
                                            <td className="p-3 text-gray-300">{ORGANIZATION_CITY_RELATION[applicant.vtc] || applicant?.gov || '-'}</td>
                                            <td className="p-3 text-gray-300">{applicant.org?.name || '-'}</td>
                                            <td className="p-3 text-gray-300">{applicant.curriculum?.code || '-'}</td>
                                            <td className="p-3 text-gray-300">{applicant.certification || '-'}</td>
                                            <td className="p-3 text-gray-300">{applicant.whatsapp ? `+2${applicant.whatsapp}` : '-'}</td>
                                            <td className="p-3 text-gray-300">{applicant.phone ? `+2${applicant.phone}` : '-'}</td>
                                            <td className="p-3 text-gray-300">
                                                {applicant.createdAt ? new Date(applicant.createdAt).toLocaleDateString('ar-EG') : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicantsDashboard;